import { useEffect, useRef, useState } from 'react'

import Icon from '../../components/icons.jsx'
import { createCompany, getCompanies, getCompanyDetails, importarEmpresas, searchEmpresasPublicas, updateCompany, updateDeal } from '../../db/api.js'
import DealCompanyCnpj from './DealCompanyCnpj.jsx'

const demo = import.meta.env.DEV && import.meta.env.MODE === 'demo'
const onlyDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 14)
const fmtCnpj = (d) => d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
const fmtDay = (v) => { if (!v) return ''; const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR') }
const yesNo = (v) => (v === true ? 'Sim' : v === false ? 'Não' : '')
const TIPO = { matriz: 'Matriz', filial: 'Filial' }
const FONTE = { apollo: 'Apollo', manual: 'Manual', import: 'Importação', scraping: 'Scraping', cnpj_publico: 'Base pública de CNPJ' }
const STATUS_EMPRESA = { prospect: 'Prospect', cliente: 'Cliente', churned: 'Churned' }
const CANAL = { phone: 'Telefone', email: 'E-mail' }
const SITUACAO = { ativa: 'Ativa', baixada: 'Baixada', suspensa: 'Suspensa', inapta: 'Inapta', nula: 'Nula' }

function companyDetailRows(c) {
  const endereco = [[c.logradouro, c.numero].filter(Boolean).join(', '), c.complemento, c.bairro].filter(Boolean).join(' · ')
  return [
    ['Razão social', c.razao_social],
    ['Nome fantasia', c.nome_fantasia],
    ['Tipo', TIPO[c.tipo] || c.tipo],
    ['Porte', c.porte],
    ['Natureza jurídica', c.natureza_juridica],
    ['Situação cadastral', [SITUACAO[c.situacao_cadastral] || c.situacao_cadastral, fmtDay(c.data_situacao_cadastral)].filter(Boolean).join(' desde ')],
    ['Motivo da situação', c.motivo_situacao_cadastral],
    ['Situação especial', [c.situacao_especial, fmtDay(c.data_situacao_especial)].filter(Boolean).join(' desde ')],
    ['Data de abertura', fmtDay(c.data_abertura)],
    ['Capital social', c.capital_social != null && c.capital_social !== '' ? Number(c.capital_social).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''],
    ['Optante MEI', yesNo(c.opcao_mei)],
    ['Optante Simples', yesNo(c.opcao_simples)],
    ['Domínio', c.dominio],
    ['Segmento', c.segmento],
    ['Tamanho', c.tamanho],
    ['Endereço', endereco],
    ['Cidade / UF', [c.cidade, c.estado].filter(Boolean).join(' / ')],
    ['CEP', c.cep],
    ['Origem do cadastro', FONTE[c.fonte] || c.fonte],
    ['Status', STATUS_EMPRESA[c.status] || c.status],
    ['Cadastrada em', fmtDay(c.created_at)],
  ]
}

export default function DealCompany({ dealId, company, onSaved, onOpenDetails, onLinkCompany, compact = false, unlinkedName = '', disabled = false }) {
  const logo = company?.logo_url || company?.avatar_url
  const [failedLogo, setFailedLogo] = useState(false)
  useEffect(() => setFailedLogo(false), [logo])

  const [open, setOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [details, setDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  useEffect(() => { setDetails(null) }, [company?.id, company?.updated_at])
  useEffect(() => {
    if (!detailsOpen || !company?.id || demo || details?.id === company.id) return undefined
    let alive = true
    setDetailsLoading(true)
    getCompanyDetails(company.id).then((d) => { if (alive) setDetails(d) }).catch(() => {}).finally(() => { if (alive) setDetailsLoading(false) })
    return () => { alive = false }
  }, [detailsOpen, company?.id, details?.id])
  const [mode, setMode] = useState('select')
  const [name, setName] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [selected, setSelected] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // Aba "Buscar CNPJ": só o número (14 dígitos) na base pública; importa e vincula.
  const [cnpj, setCnpj] = useState('')
  const [found, setFound] = useState(null)
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const root = useRef(null)
  const trigger = useRef(null)

  useEffect(() => {
    if (!open) return
    const outside = (event) => { if (!saving && !root.current?.contains(event.target)) setOpen(false) }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open, saving])

  useEffect(() => {
    if (!open || mode !== 'select') return
    let alive = true
    setLoading(true)
    const timer = setTimeout(() => {
      const request = demo
        ? import('../../demo/data.js').then((m) => m.getDemoCompanies().filter((item) => item.nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(search.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase())))
        : getCompanies(search)
      request
        .then((data) => { if (alive) setRows(Array.isArray(data) ? data : data.items || []) })
        .catch((err) => { if (alive) setError(err.message) })
        .finally(() => { if (alive) setLoading(false) })
    }, 180)
    return () => { alive = false; clearTimeout(timer) }
  }, [open, mode, search])

  useEffect(() => {
    if (!open || mode !== 'cnpj') return
    setFound(null)
    if (cnpj.length !== 14) return
    let alive = true
    setCnpjLoading(true); setError('')
    searchEmpresasPublicas({ cnpj })
      .then((data) => {
        if (!alive) return
        const item = data.items?.[0]
        if (item) setFound(item)
        else setError('CNPJ não encontrado na base pública.')
      })
      .catch((err) => { if (alive) setError(err.status === 503 ? 'Base pública de CNPJ indisponível no momento.' : err.message) })
      .finally(() => { if (alive) setCnpjLoading(false) })
    return () => { alive = false }
  }, [open, mode, cnpj])

  const save = async (event) => {
    event.preventDefault()
    if (saving) return
    setError(''); setSaving(true)
    try {
      let saved
      if (demo && !onLinkCompany) {
        const { saveDemoDealCompany } = await import('../../demo/data.js')
        saved = saveDemoDealCompany(dealId, { mode, name: name.trim(), companyId: selected })
      } else if (mode === 'cnpj') {
        // Import é idempotente por CNPJ: se a empresa já existe na organização,
        // devolve a mesma (atualizada) em vez de duplicar.
        const { imported } = await importarEmpresas([found.cnpj])
        saved = imported?.[0]
        if (!saved) throw new Error('Não foi possível importar a empresa.')
        if (!onLinkCompany) await updateDeal(dealId, { company_id: saved.id })
      } else if (mode === 'edit') {
        saved = await updateCompany(company.id, { nome: name.trim() })
      } else {
        saved = mode === 'create'
          ? await createCompany({ nome: name.trim() })
          : rows.find((item) => item.id === selected)
        if (!saved) throw new Error('Empresa não encontrada.')
        if (!onLinkCompany) await updateDeal(dealId, { company_id: saved.id })
      }
      if (onLinkCompany) await onLinkCompany(saved)
      onSaved(saved)
      setOpen(false); trigger.current?.focus()
    } catch (err) { setError(err.message) }
    finally { setSaving(false) }
  }

  return <div className="dp-owners dp-company-card" ref={root} onKeyDown={(event) => { if (event.key === 'Escape' && open) { event.stopPropagation(); if (!saving) { setOpen(false); trigger.current?.focus() } } }}>
    {compact ? <div className="at-company-inline">
      {company?.id && <button type="button" className="at-company-open" aria-label="Abrir empresa" title="Abrir empresa" onClick={onOpenDetails}><Icon n="external" s={12} /></button>}
      <button type="button" ref={trigger} className="at-company-select" disabled={disabled} aria-expanded={open} aria-label={company || unlinkedName ? 'Alterar empresa do contato' : 'Adicionar empresa'} onClick={() => { setOpen(!open); setMode(company ? 'edit' : 'select'); setName(company?.nome || ''); setSelected(company?.id || ''); setSearch(''); setError('') }}>
        <span>{company?.nome || unlinkedName || 'Adicionar empresa'}</span><Icon n={company || unlinkedName ? 'chevron_d' : 'plus'} s={12} />
      </button>
    </div> : <>
    <div className="dp-identity-row"><button type="button" className="dp-avatar-link" aria-label="Ver detalhes da empresa" onClick={() => { setOpen(false); onOpenDetails?.() }}><span className="dp-owner-avatar dp-company-avatar" aria-hidden="true">{logo && !failedLogo ? <img src={logo} alt="" onError={() => setFailedLogo(true)} /> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 7h1m4 0h1M9 11h1m4 0h1M10 21v-6h4v6" /></svg>}</span></button><button type="button" ref={trigger} className="dp-owners-trigger" aria-expanded={open} aria-label="Alterar empresa" onClick={() => { setOpen(!open); setMode(company ? 'edit' : 'select'); setName(company?.nome || ''); setSelected(company?.id || ''); setSearch(''); setError('') }}>
      <span><span className="cv-row-label">Empresa</span><span className="cv-row-value">{company?.nome || 'Selecionar empresa'}</span></span><span className="dp-owners-chevron" aria-hidden="true"><Icon n="chevron_d" s={14} /></span>
    </button></div>
    {company && <DealCompanyCnpj dealId={dealId} company={company} onSaved={onSaved} />}
    {company && <div className="dp-company-cnpj">
      {detailsOpen && <div className="dp-company-extra">
        {detailsLoading && !details && <div><span className="cv-row-value">Carregando detalhes…</span></div>}
        {companyDetailRows({ ...company, ...(details || {}) }).map(([label, value]) => <div key={label}><span className="cv-row-label">{label}</span><span className="cv-row-value">{value || 'Não informado'}</span></div>)}
        {!!details?.cnaes?.length && <div><span className="cv-row-label">Atividades (CNAE)</span>{details.cnaes.map((c) => <span key={c.codigo} className="cv-row-value">{c.codigo} — {c.descricao}{c.is_principal ? ' (principal)' : ''}</span>)}</div>}
        {!!details?.socios?.length && <div><span className="cv-row-label">Sócios</span>{details.socios.map((p) => <span key={p.id} className="cv-row-value">{p.nome}{p.qualificacao ? ` · ${p.qualificacao}` : ''}</span>)}</div>}
        {!!details?.contatos?.length && <div><span className="cv-row-label">Contatos da empresa</span>{details.contatos.map((c) => <span key={c.id} className="cv-row-value">{CANAL[c.channel_type] || c.channel_type}: {c.value}{c.is_primary ? ' (principal)' : ''}</span>)}</div>}
      </div>}
      <button type="button" className="dp-company-more" aria-expanded={detailsOpen} onClick={() => setDetailsOpen(!detailsOpen)}>{detailsOpen ? 'Ver menos' : 'Ver mais detalhes'}</button>
    </div>}
    </>}
    {open && <form className="dp-owners-menu dp-lead-menu" onSubmit={save}>
      <div className="dp-lead-tabs">{[['edit','Editar nome'],['select','Selecionar'],['cnpj','Buscar CNPJ'],['create','Nova Empresa']].map(([value,label]) => <button type="button" key={value} disabled={saving || (value === 'edit' && !company)} aria-pressed={mode === value} onClick={() => { setMode(value); setName(value === 'edit' ? company?.nome || '' : ''); setCnpj(''); setFound(null); setError('') }}>{label}</button>)}</div>
      {mode === 'cnpj' ? <><input autoFocus aria-label="CNPJ" inputMode="numeric" placeholder="Digite o CNPJ (só números)" maxLength={18} value={fmtCnpj(cnpj)} disabled={saving} onChange={(event) => { setCnpj(onlyDigits(event.target.value)); setError('') }} /><div className="dp-lead-options" role="status">{cnpj.length < 14 ? <p>Digite os 14 dígitos do CNPJ.</p> : cnpjLoading ? <p>Buscando na base pública…</p> : found ? <button type="button" aria-pressed="true" disabled={saving}><strong>{found.nome_fantasia || found.razao_social}</strong>{found.nome_fantasia && <><br /><small>{found.razao_social}</small></>}<br /><small>{[found.cidade, found.uf].filter(Boolean).join(' / ')}{found.situacao_cadastral ? ` · ${SITUACAO[found.situacao_cadastral] || found.situacao_cadastral}` : ''}{found.already_imported ? ' · já está na organização' : ''}</small></button> : null}</div></> : mode === 'select' ? <><input aria-label="Buscar empresa" placeholder="Buscar empresa…" value={search} onChange={(event) => { setSearch(event.target.value); setSelected('') }} /><div className="dp-lead-options" role="group" aria-label="Empresas disponíveis">{loading ? <p>Carregando…</p> : rows.length ? rows.map((item) => <button type="button" key={item.id} aria-pressed={selected === item.id} disabled={saving} onClick={() => setSelected(item.id)}>{item.nome}</button>) : <p>Nenhuma empresa encontrada.</p>}</div></> : <label className="dp-lead-name">{mode === 'edit' ? 'Nome da empresa' : 'Nome da nova empresa'}<input autoFocus required maxLength={200} value={name} disabled={saving} onChange={(event) => setName(event.target.value)} /></label>}
      {error && <p role="alert">{error}</p>}
      <footer><button type="button" disabled={saving} onClick={() => setOpen(false)}>Cancelar</button><button type="submit" disabled={saving || (mode === 'select' ? !selected || loading : mode === 'cnpj' ? !found || cnpjLoading : !name.trim())}>{saving ? 'Salvando…' : mode === 'create' ? 'Criar e vincular' : mode === 'cnpj' ? (found?.already_imported ? 'Vincular' : 'Importar e vincular') : 'Salvar'}</button></footer>
    </form>}
  </div>
}
